/**
 * Recommendation Notification Service
 * Manages when to show card recommendation notifications to users
 */

class RecommendationNotificationService {
  constructor() {
    this.STORAGE_KEY = 'zoobbe_recommendation_notifications';
    this.NOTIFICATION_INTERVAL = 6 * 60 * 60 * 1000; // 12 hours in milliseconds
    this.MIN_VISIT_INTERVAL = 30 * 60 * 1000; // 30 minutes minimum between notifications
  }

  /**
   * Check if we should show a recommendation notification for a board
   */
  shouldShowNotification(boardId) {
    try {
      const data = this.getStoredData();
      const boardData = data[boardId];
      
      if (!boardData) {
        // First time visiting this board
        return true;
      }

      const now = Date.now();
      const lastShown = boardData.lastShown || 0;
      const lastDismissed = boardData.lastDismissed || 0;
      const lastVisit = boardData.lastVisit || 0;

      // Don't show if dismissed recently (within 12 hours)
      if (lastDismissed && (now - lastDismissed) < this.NOTIFICATION_INTERVAL) {
        return false;
      }

      // Don't show if shown recently (within 30 minutes)
      if (lastShown && (now - lastShown) < this.MIN_VISIT_INTERVAL) {
        return false;
      }

      // Show if it's been more than 12 hours since last shown
      if (!lastShown || (now - lastShown) >= this.NOTIFICATION_INTERVAL) {
        return true;
      }

      // Show if it's a new visit after some time
      if (lastVisit && (now - lastVisit) >= this.NOTIFICATION_INTERVAL) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking notification status:', error);
      return true; // Default to showing notification on error
    }
  }

  /**
   * Mark that a notification was shown for a board
   */
  markNotificationShown(boardId) {
    try {
      const data = this.getStoredData();
      const now = Date.now();
      
      data[boardId] = {
        ...data[boardId],
        lastShown: now,
        lastVisit: now
      };
      
      this.saveData(data);
    } catch (error) {
      console.error('Error marking notification shown:', error);
    }
  }

  /**
   * Mark that a notification was dismissed for a board
   */
  markNotificationDismissed(boardId) {
    try {
      const data = this.getStoredData();
      const now = Date.now();
      
      data[boardId] = {
        ...data[boardId],
        lastDismissed: now,
        lastVisit: now
      };
      
      this.saveData(data);
    } catch (error) {
      console.error('Error marking notification dismissed:', error);
    }
  }

  /**
   * Mark that a user visited a board (without showing notification)
   */
  markBoardVisit(boardId) {
    try {
      const data = this.getStoredData();
      const now = Date.now();
      
      data[boardId] = {
        ...data[boardId],
        lastVisit: now
      };
      
      this.saveData(data);
    } catch (error) {
      console.error('Error marking board visit:', error);
    }
  }

  /**
   * Get stored notification data
   */
  getStoredData() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error reading stored notification data:', error);
      return {};
    }
  }

  /**
   * Save notification data to localStorage
   */
  saveData(data) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving notification data:', error);
    }
  }

  /**
   * Clear all notification data (for testing or reset)
   */
  clearAllData() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing notification data:', error);
    }
  }

  /**
   * Get notification status for a board (for debugging)
   */
  getBoardStatus(boardId) {
    const data = this.getStoredData();
    const boardData = data[boardId] || {};
    const now = Date.now();
    
    return {
      boardId,
      lastShown: boardData.lastShown ? new Date(boardData.lastShown).toISOString() : null,
      lastDismissed: boardData.lastDismissed ? new Date(boardData.lastDismissed).toISOString() : null,
      lastVisit: boardData.lastVisit ? new Date(boardData.lastVisit).toISOString() : null,
      shouldShow: this.shouldShowNotification(boardId),
      timeSinceLastShown: boardData.lastShown ? now - boardData.lastShown : null,
      timeSinceLastDismissed: boardData.lastDismissed ? now - boardData.lastDismissed : null
    };
  }
}

// Export singleton instance
export default new RecommendationNotificationService();
